import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedTenantAdminRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedTenantAdminRoute Component
 *
 * Restricts access to ANY tenant admin (not just Platform Admin).
 * Regular users/members will see "Access Denied" page.
 *
 * Admin Check:
 * - User must be authenticated
 * - User must have role = 'admin'
 * - Works for ANY tenant (not restricted to Platform Tenant)
 *
 * Note: Frontend check for UX only. Backend enforces security via role check in controller.
 */
export const ProtectedTenantAdminRoute: React.FC<ProtectedTenantAdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin (any tenant)
  const isAdmin = user?.role === 'admin';

  // Not admin - show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 bg-card rounded-lg border border-border max-w-md">
          <h2 className="text-2xl font-bold text-foreground mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-2">Admin access required.</p>
          <p className="text-sm text-muted-foreground">
            This area is only accessible to administrators.
          </p>
        </div>
      </div>
    );
  }

  // Admin - render children
  return <>{children}</>;
};
